import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tooltip,
    Typography,
    useMediaQuery
} from "@mui/material";
import { InfoOutlined, LockOutlined, WarningAmber } from "@mui/icons-material";
import { toast } from "sonner";
import type { WorkProduct } from "../../types";
import type { SddProgressReport, SddProgressSummary } from "@/api/api";
import { getWorkProductBridgeMetadata } from "./FeatureStudioCard";
import {
    FeatureLayersIcon,
    FeatureStorySelectionPage,
    FeatureStudioWorkflowOverview,
    ProductCubeIcon,
    ProductHierarchyPage,
    ProgressDonut,
    SddProgressReportPage,
    ScopeProgressRings,
    StoryGridIcon,
    type ReportExporter,
    type ReportLoader,
    progressForScope
} from "./FeatureStudioWorkflowOverview";
import {
    SDD_STACKED_WORK_PRODUCT_LIST_HEIGHT,
    SDD_WORK_PRODUCT_STACK_QUERY,
    SddRequiredWorkProducts,
    WorkProductAcceptanceButton,
    workProductScrollbarSx
} from "./SddRequiredWorkProducts";
import { SddWorkflowStepper } from "./SddWorkflowStepper";
import {
    ALL_SDD_REQUIRED_WORK_PRODUCTS,
    SDD_REQUIRED_WORK_PRODUCTS,
    SDD_STAGES,
    type SddHierarchyLevel,
    type SddStageKey,
    canStartImplementationFromWorkProducts,
    findRequiredWorkProductSource,
    getSddWorkProductDescription,
    isFirstDraftReady
} from "./sddWorkflow";

type ScopeKey = "PRODUCT" | "FEATURE" | "USERSTORY";
type GenerationScope = { level: ScopeKey; featureId: number | null; userStoryId: string | null };
type WorkProductUiState = { state: "idle" | "generating" | "ready"; step?: string; progress?: number | null };
type RecommendedWorkProductRow = {
    workProduct: WorkProduct;
    scope: GenerationScope;
    state: WorkProductUiState;
    ready: boolean;
};

const STAGE_TITLES: Record<SddStageKey, string> = {
    specify: "Specify",
    plan: "Plan",
    task_breakdown: "Define Tasks",
    implement: "Implement",
    validate: "Validate"
};

const LAPTOP_FIT_QUERY = "@media (min-width: 1280px) and (min-height: 768px)";

function entityId(entity: any): string {
    return String(entity?.featureid || entity?.user_story_id || entity?.id || entity?.story_id || "");
}

function entityName(entity: any): string {
    return entity?.feature_name || entity?.title || entity?.name || "Not selected";
}

function workProductMatchesScope(workProduct: WorkProduct, scope: ScopeKey): boolean {
    const loadedScope = workProduct.scope_level?.toUpperCase();
    if (loadedScope) return loadedScope === scope;
    const levels = workProduct.display_level?.map(level => level.toUpperCase()) || [];
    const declared = workProduct.level?.toUpperCase();
    if (!levels.length && !declared) return true;
    return levels.includes(scope) || declared === scope;
}

function scopeForOptionalWorkProduct(workProduct: WorkProduct, selectedScope: GenerationScope): GenerationScope | null {
    if (workProductMatchesScope(workProduct, "PRODUCT")) {
        return { level: "PRODUCT", featureId: null, userStoryId: null };
    }
    if (selectedScope.featureId && workProductMatchesScope(workProduct, "FEATURE")) {
        return { level: "FEATURE", featureId: selectedScope.featureId, userStoryId: null };
    }
    if (selectedScope.featureId && selectedScope.userStoryId && workProductMatchesScope(workProduct, "USERSTORY")) {
        return selectedScope;
    }
    return null;
}

function workProductStage(workProduct: WorkProduct): SddStageKey | null {
    const value = String(workProduct.phase || "")
        .trim()
        .toLowerCase();
    if (["discovery", "specify"].includes(value)) return "specify";
    if (["design", "plan"].includes(value)) return "plan";
    if (["task-breakdown", "task breakdown", "task_breakdown"].includes(value)) return "task_breakdown";
    if (["code", "implement", "implementation"].includes(value)) return "implement";
    if (["qa", "validate", "validation"].includes(value)) return "validate";
    return null;
}

function scopeForLevel(level: SddHierarchyLevel, feature: any, story: any): GenerationScope {
    const featureId = Number(feature?.featureid || feature?.id || 0) || null;
    const storyId = story?.id ? String(story.id) : null;
    if (level === "product") return { level: "PRODUCT", featureId: null, userStoryId: null };
    if (level === "feature") return { level: "FEATURE", featureId, userStoryId: null };
    return { level: "USERSTORY", featureId, userStoryId: storyId };
}

function scopeHasIdentity(scope: GenerationScope): boolean {
    return scope.level === "PRODUCT" || Boolean(scope.featureId && (scope.level !== "USERSTORY" || scope.userStoryId));
}

function ScopeProgressCard({
    type,
    name,
    description,
    scopeProgress,
    children
}: {
    type: "Product" | "Feature" | "Story";
    name: string;
    description: string;
    scopeProgress?: SddProgressSummary | null;
    children?: React.ReactNode;
}) {
    const icon =
        type === "Product" ? <ProductCubeIcon /> : type === "Feature" ? <FeatureLayersIcon /> : <StoryGridIcon />;
    const ringSize = type === "Product" ? 38 : type === "Feature" ? 32 : 30;
    const titleSize = type === "Product" ? 12.5 : type === "Feature" ? 12 : 11.5;
    const descriptionSize = type === "Story" ? 10 : 10.5;
    return (
        <Box
            sx={{
                p: type === "Product" ? "11px" : type === "Feature" ? "10px" : "9px",
                flex: "1 1 auto",
                display: "flex",
                flexDirection: "column",
                border: "1.5px solid",
                borderColor: type === "Product" ? "#103699" : type === "Feature" ? "rgba(2,132,199,.45)" : "rgba(5,150,105,.45)",
                borderRadius: type === "Product" ? "12px" : type === "Feature" ? "11px" : "10px",
                bgcolor: type === "Product" ? "#fff" : type === "Feature" ? "#f1f5f9" : "#f8fafc",
                [LAPTOP_FIT_QUERY]: {
                    p: type === "Product" ? "7px" : "6px"
                }
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                {icon}
                <Typography noWrap title={`${type} — ${name}`} sx={{ minWidth: 0, flex: 1, fontSize: titleSize, fontWeight: 600 }}>
                    {type} — {name}
                </Typography>
            </Box>
            <Typography sx={{ mt: "2px", mb: "9px", fontSize: descriptionSize, lineHeight: 1.35, color: "text.secondary", [LAPTOP_FIT_QUERY]: { mb: "4px", lineHeight: 1.2 } }}>{description}</Typography>
            {scopeProgress ? (
                <ScopeProgressRings scopeProgress={scopeProgress} size={ringSize} compact />
            ) : (
                <Typography sx={{ py: 0.5, fontSize: 9.5, color: "text.secondary" }}>Progress unavailable</Typography>
            )}
            {children}
        </Box>
    );
}

function ProgressHierarchy({ project, feature, story, progress }: { project: any; feature: any; story: any; progress?: SddProgressReport | null }) {
    const productProgress = progressForScope(progress);
    const emptyProgress = progress?.scope_progress?.empty || null;
    const featureProgress = feature ? progressForScope(progress, feature) : emptyProgress;
    const storyProgress = feature && story ? progressForScope(progress, feature, story) : emptyProgress;
    return (
        <Box
            component="aside"
            sx={{
                minWidth: 0,
                minHeight: 500,
                mt: { xs: 0, lg: "24px" },
                height: { xs: "100%", lg: "calc(100% - 24px)" },
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                border: "1px solid rgba(15,23,42,.10)",
                borderRadius: "12px",
                bgcolor: "background.paper",
                p: 2,
                overflow: "hidden",
                [LAPTOP_FIT_QUERY]: {
                    minHeight: 0,
                    mt: 0,
                    height: "100%",
                    p: 1,
                    overflowY: "auto"
                }
            }}
        >
            <Typography sx={{ mb: 0.75, pb: 1.25, borderBottom: "1px solid #e2e8f0", fontSize: 10, fontWeight: 800, letterSpacing: ".09em", textTransform: "uppercase", color: "#64748b" }}>
                Progress on SDD Steps and Work Products
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                <ScopeProgressCard
                    type="Product"
                    name={project?.project_name || project?.name || "Selected product"}
                    description="Progress on all features & stories for this product."
                    scopeProgress={productProgress}
                >
                    <Box sx={{ mt: "11px", flex: 1, display: "flex", flexDirection: "column" }}>
                        <ScopeProgressCard
                            type="Feature"
                            name={feature ? entityName(feature) : "Select a Feature"}
                            description={feature ? "Progress on all of this feature's stories." : "Select a Feature to view its stage completion percentages."}
                            scopeProgress={featureProgress}
                        >
                            <Box sx={{ mt: "9px", flex: 1, display: "flex", flexDirection: "column" }}>
                                <ScopeProgressCard
                                    type="Story"
                                    name={story ? entityName(story) : "Select a Story"}
                                    description={story ? "Progress on this story's SDD steps." : "Select a Story to view its stage completion percentages."}
                                    scopeProgress={storyProgress}
                                />
                            </Box>
                        </ScopeProgressCard>
                    </Box>
                </ScopeProgressCard>
            </Box>
        </Box>
    );
}

function RecommendedWorkProductsPanel({
    stage,
    rows,
    loading,
    pending,
    onGenerate,
    onOpen,
    onApprove,
    onBuildAll,
    onAcceptAll
}: {
    stage: SddStageKey;
    rows: RecommendedWorkProductRow[];
    loading: boolean;
    pending: "build" | "accept" | null;
    onGenerate?: (workProduct: WorkProduct, scope: GenerationScope) => Promise<void> | void;
    onOpen: (workProduct: WorkProduct, scope?: GenerationScope) => void;
    onApprove: (workProduct: WorkProduct, approved: boolean, scope: GenerationScope) => Promise<boolean | void> | boolean | void;
    onBuildAll: () => void;
    onAcceptAll: () => void;
}) {
    const readyCount = rows.filter(row => row.ready && !row.workProduct.approved).length;

    return (
        <Box
            component="section"
            aria-labelledby="recommended-work-products-title"
            sx={{
                minWidth: 0,
                [LAPTOP_FIT_QUERY]: { height: "100%", minHeight: 0, display: "flex", flexDirection: "column" },
                [SDD_WORK_PRODUCT_STACK_QUERY]: { height: "auto", mt: 3 }
            }}
        >
            <Box
                sx={{
                    minHeight: 116,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    flexShrink: 0,
                    [LAPTOP_FIT_QUERY]: { minHeight: 78 }
                }}
            >
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap", [SDD_WORK_PRODUCT_STACK_QUERY]: { mt: "14px" } }}>
                    <Typography
                        id="recommended-work-products-title"
                        sx={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#475569" }}
                    >
                        Create Recommended Work Products
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                        {rows.length} recommended · {readyCount} ready for review
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!rows.some(row => row.state.state === "idle") || pending !== null}
                        startIcon={pending === "build" ? <CircularProgress size={11} color="inherit" /> : undefined}
                        onClick={onBuildAll}
                        sx={{ minWidth: 84, width: "auto", height: 31, px: 1.5, flexShrink: 0, whiteSpace: "nowrap", fontSize: 11.5, fontWeight: 600, textTransform: "none" }}
                    >
                        Build All
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!readyCount || pending !== null}
                        startIcon={pending === "accept" ? <CircularProgress size={11} color="inherit" /> : undefined}
                        onClick={onAcceptAll}
                        sx={{ minWidth: 84, width: "auto", height: 31, px: 1.5, flexShrink: 0, whiteSpace: "nowrap", fontSize: 11.5, fontWeight: 600, textTransform: "none" }}
                    >
                        Accept All
                    </Button>
                </Box>
                <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: "#64748b" }}>
                    Additional work products for the {STAGE_TITLES[stage]} step to provide more detail.
                </Typography>
            </Box>
            <Box
                data-testid="recommended-work-products-list"
                sx={{
                    height: 300,
                    overflowY: "auto",
                    pr: 0.5,
                    ...workProductScrollbarSx,
                    [LAPTOP_FIT_QUERY]: { height: "auto", flex: 1, minHeight: 0 },
                    [SDD_WORK_PRODUCT_STACK_QUERY]: {
                        height: SDD_STACKED_WORK_PRODUCT_LIST_HEIGHT,
                        minHeight: SDD_STACKED_WORK_PRODUCT_LIST_HEIGHT,
                        flex: "0 0 auto"
                    }
                }}
            >
                {loading ? (
                    <Typography sx={{ py: 2, fontSize: 12, lineHeight: 1.5, color: "text.secondary" }}>Loading recommended work products…</Typography>
                ) : rows.length === 0 ? (
                    <Typography sx={{ py: 2, fontSize: 12, lineHeight: 1.5, color: "text.secondary" }}>
                        No additional recommended work products are configured for this step and scope.
                    </Typography>
                ) : (
                    rows.map(({ workProduct, scope, state, ready }) => {
                        const generating = state.state === "generating";
                        const accepted = Boolean(workProduct.approved);
                        const level: ScopeKey = scope.level;
                        const levelLabel = level === "USERSTORY" ? "Story" : level[0] + level.slice(1).toLowerCase();
                        const color = level === "PRODUCT" ? "#103699" : level === "FEATURE" ? "#0284c7" : "#059669";
                        const context =
                            level === "USERSTORY" ? `userstory-${scope.userStoryId}` : level === "FEATURE" ? `feature-${scope.featureId}` : "product";
                        const bridge = getWorkProductBridgeMetadata({
                            workProductId: String(workProduct.id),
                            workProductName: workProduct.name,
                            state: generating ? "generating" : ready ? "ready" : "idle",
                            context,
                            phase: SDD_STAGES.find(item => item.key === stage)?.phase || "discovery"
                        });
                        return (
                            <Box
                                key={`${workProduct.id}:${scope.level}:${scope.featureId || ""}:${scope.userStoryId || ""}`}
                                data-ui-bridge={bridge.elementId}
                                data-ui-label={bridge.uiLabel}
                                data-ui-role={bridge.uiRole}
                                data-ui-group={bridge.uiGroup}
                                data-ui-description={bridge.uiDescription}
                                data-work-product-id={String(workProduct.id)}
                                data-work-product-name={workProduct.name}
                                data-work-product-level={scope.level}
                                data-work-product-feature-id={scope.featureId ?? undefined}
                                data-work-product-user-story-id={scope.userStoryId ?? undefined}
                                sx={{
                                    minHeight: level === "USERSTORY" ? 38 : 44,
                                    display: "grid",
                                    gridTemplateColumns: "18px 56px minmax(0,1fr) auto 30px",
                                    alignItems: "center",
                                    gap: 0.75,
                                    borderBottom: "1px solid #e5e7eb",
                                    py: level === "USERSTORY" ? 0.35 : 0.7
                                }}
                            >
                                <Tooltip title={getSddWorkProductDescription(workProduct)}>
                                    <InfoOutlined aria-label={`About ${workProduct.name}`} sx={{ fontSize: 15, color: "#94a3b8" }} />
                                </Tooltip>
                                <Typography sx={{ position: "relative", top: 2, fontSize: 10.5, lineHeight: 1.6, fontWeight: 700, color }}>{levelLabel}</Typography>
                                <Typography noWrap title={workProduct.name} sx={{ position: "relative", top: 2, fontSize: 12.5, lineHeight: 1.6, fontWeight: 600, color: "#0f172a" }}>
                                    {workProduct.name}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={generating || (!ready && !onGenerate)}
                                    startIcon={generating ? <CircularProgress size={11} color="inherit" /> : undefined}
                                    aria-label={`${generating ? "Generating" : ready ? "Open" : "Generate"} ${workProduct.name}`}
                                    onClick={() => (ready ? onOpen(workProduct, scope) : onGenerate?.(workProduct, scope))}
                                    sx={{ minWidth: 76, height: 28, px: 1, fontSize: 10.5, textTransform: "none" }}
                                >
                                    {generating ? "Building…" : ready ? "View/Edit" : "Build"}
                                </Button>
                                <WorkProductAcceptanceButton
                                    accepted={accepted}
                                    disabled={!ready || generating}
                                    name={workProduct.name}
                                    onToggle={() => onApprove(workProduct, !accepted, scope)}
                                />
                            </Box>
                        );
                    })
                )}
            </Box>
        </Box>
    );
}

export function SddWorkspace({
    project,
    feature,
    story,
    workProductsByPhase,
    requiredWorkProductsByPhase,
    onGenerate,
    onOpen,
    onApprove,
    getWorkProductState,
    onFeatureSelect,
    onStorySelect,
    optionalFeature,
    optionalStory,
    optionalWorkProductsByPhase,
    optionalWorkProductsLoading = false,
    onOptionalFeatureSelect,
    onOptionalStorySelect,
    onGenerateOptional,
    getOptionalWorkProductState,
    contextMesh,
    progress,
    progressLoading = false,
    onReviewsChanged,
    onHierarchyPageOpen,
    hierarchyPageOpen: controlledHierarchyPageOpen,
    onHierarchyPageOpenChange,
    scopePickerPageOpen: controlledScopePickerPageOpen,
    onScopePickerOpenChange,
    progressReportPageOpen: controlledProgressReportPageOpen,
    onProgressReportOpenChange,
    loadProgressReport,
    exportProgressReport
}: {
    project: any;
    feature: any;
    story: any;
    workProductsByPhase: Record<string, WorkProduct[]>;
    requiredWorkProductsByPhase?: Record<string, WorkProduct[]>;
    onGenerate: (workProduct: WorkProduct, scope?: GenerationScope) => Promise<void> | void;
    onOpen: (workProduct: WorkProduct, scope?: GenerationScope) => void;
    onApprove: (workProduct: WorkProduct, approved: boolean, scope: GenerationScope) => Promise<boolean | void> | boolean | void;
    getWorkProductState: (workProduct: WorkProduct, scope?: GenerationScope) => WorkProductUiState;
    onFeatureSelect?: (feature: any) => void;
    onStorySelect?: (story: any) => void;
    optionalFeature?: any;
    optionalStory?: any;
    optionalWorkProductsByPhase?: Record<string, WorkProduct[]>;
    optionalWorkProductsLoading?: boolean;
    onOptionalFeatureSelect?: (feature: any) => void;
    onOptionalStorySelect?: (story: any, feature?: any) => void;
    onGenerateOptional?: (workProduct: WorkProduct, scope: GenerationScope) => Promise<void> | void;
    getOptionalWorkProductState?: (workProduct: WorkProduct, scope: GenerationScope) => WorkProductUiState;
    contextMesh?: React.ReactNode;
    progress?: SddProgressReport | null;
    progressLoading?: boolean;
    onReviewsChanged?: () => Promise<void> | void;
    onAddFeature?: () => void;
    onAddStory?: () => void;
    onCreateFromHierarchy?: (context: { project: any; feature?: any }) => void;
    onEditFeature?: (feature: any) => void;
    onEditStory?: (feature: any, story: any) => void;
    onHierarchyPageOpen?: () => void;
    hierarchyPageOpen?: boolean;
    onHierarchyPageOpenChange?: (open: boolean) => void;
    scopePickerPageOpen?: boolean;
    onScopePickerOpenChange?: (open: boolean) => void;
    progressReportPageOpen?: boolean;
    onProgressReportOpenChange?: (open: boolean) => void;
    loadProgressReport?: ReportLoader;
    exportProgressReport?: ReportExporter;
}) {
    const [selectedStage, setSelectedStage] = useState<SddStageKey>("specify");
    useEffect(() => {
        const handleStageNavigation = (event: Event) => {
            const detail = (event as CustomEvent<{ name?: string; stage?: string }>).detail;
            if (detail?.name !== "navigate_to_sdd_stage") return;
            if (SDD_STAGES.some(stage => stage.key === detail.stage)) setSelectedStage(detail.stage as SddStageKey);
        };
        window.addEventListener("fde:sdd-navigation", handleStageNavigation);
        return () => window.removeEventListener("fde:sdd-navigation", handleStageNavigation);
    }, []);
    const [internalHierarchyPageOpen, setInternalHierarchyPageOpen] = useState(false);
    const hierarchyPageOpen = controlledHierarchyPageOpen ?? internalHierarchyPageOpen;
    const setHierarchyPageOpen = (open: boolean) => {
        setInternalHierarchyPageOpen(open);
        onHierarchyPageOpenChange?.(open);
    };
    const [internalScopePickerPageOpen, setInternalScopePickerPageOpen] = useState(false);
    const scopePickerPageOpen = controlledScopePickerPageOpen ?? internalScopePickerPageOpen;
    const setScopePickerPageOpen = (open: boolean) => {
        setInternalScopePickerPageOpen(open);
        onScopePickerOpenChange?.(open);
    };
    const [internalProgressReportPageOpen, setInternalProgressReportPageOpen] = useState(false);
    const progressReportPageOpen = controlledProgressReportPageOpen ?? internalProgressReportPageOpen;
    const setProgressReportPageOpen = (open: boolean) => {
        setInternalProgressReportPageOpen(open);
        onProgressReportOpenChange?.(open);
    };
    const [skipReviewOpen, setSkipReviewOpen] = useState(false);
    const [requiredPending, setRequiredPending] = useState<"build" | "accept" | null>(null);
    const [recommendedPending, setRecommendedPending] = useState<"build" | "accept" | null>(null);
    const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
    const selectedDefinition = SDD_STAGES.find(stage => stage.key === selectedStage) || SDD_STAGES[0];
    const requiredWorkProducts = useMemo(
        () => Object.values(requiredWorkProductsByPhase || workProductsByPhase).flat(),
        [requiredWorkProductsByPhase, workProductsByPhase]
    );
    const activeLevel: SddHierarchyLevel = story?.id ? "story" : feature ? "feature" : "product";
    const canImplement = activeLevel === "story" && canStartImplementationFromWorkProducts(activeLevel, requiredWorkProducts);
    const completedDrafts = useMemo(
        () =>
            new Set<SddStageKey>(
                SDD_STAGES.filter(stage => {
                    const definitions = SDD_REQUIRED_WORK_PRODUCTS[stage.key].filter(workProduct => workProduct.level === activeLevel);
                    return (
                        definitions.length > 0 && definitions.every(definition => isFirstDraftReady(findRequiredWorkProductSource(definition, requiredWorkProducts)))
                    );
                }).map(stage => stage.key)
            ),
        [activeLevel, requiredWorkProducts]
    );
    const implementationLocked = selectedStage === "implement" && !canImplement;

    const requiredRows = SDD_REQUIRED_WORK_PRODUCTS[selectedStage]
        .map(definition => {
            const workProduct = findRequiredWorkProductSource(definition, requiredWorkProducts);
            const scope = scopeForLevel(definition.level, feature, story);
            const state = workProduct ? getWorkProductState(workProduct, scope) : { state: "idle" as const };
            const progressRow = progress?.rows.find(
                row =>
                    row.canonical_variable === definition.variable &&
                    row.level === scope.level &&
                    String(row.feature_id || "") === String(scope.featureId || "") &&
                    String(row.story_id || "") === String(scope.userStoryId || "")
            );
            const ready = Boolean(
                workProduct &&
                (state.state === "ready" || isFirstDraftReady(workProduct) || progressRow?.status === "in_progress" || progressRow?.status === "accepted")
            );
            const accepted = progressRow ? progressRow.status === "accepted" : Boolean(workProduct?.approved);
            return { definition, workProduct, scope, state, ready, accepted };
        })
        .filter(row => row.workProduct && scopeHasIdentity(row.scope));
    const requiredToBuild = requiredRows.filter(row => row.state.state === "idle" && !row.ready);
    const requiredToAccept = requiredRows.filter(row => row.ready && !row.accepted);

    const optionalFeatureSelection = optionalFeature === undefined ? feature : optionalFeature;
    const optionalStorySelection = optionalStory === undefined ? story : optionalStory;
    const optionalScope: GenerationScope = optionalStorySelection?.id
        ? {
              level: "USERSTORY",
              featureId: Number(optionalFeatureSelection?.featureid || optionalFeatureSelection?.id || 0) || null,
              userStoryId: String(optionalStorySelection.id)
          }
        : optionalFeatureSelection
          ? { level: "FEATURE", featureId: Number(optionalFeatureSelection?.featureid || optionalFeatureSelection?.id || 0) || null, userStoryId: null }
          : { level: "PRODUCT", featureId: null, userStoryId: null };
    const optionalCatalogProducts = useMemo(() => {
        const seen = new Set<string>();
        return Object.values(optionalWorkProductsByPhase || workProductsByPhase)
            .flat()
            .filter(workProduct => !workProduct.name.includes("(HIDDEN)"))
            .filter(workProduct => {
                const key = `${workProduct.id}:${workProduct.scope_level || workProduct.level || workProduct.display_level?.join(",") || ""}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .filter(source => !ALL_SDD_REQUIRED_WORK_PRODUCTS.some(definition => Boolean(findRequiredWorkProductSource(definition, [source]))));
    }, [optionalWorkProductsByPhase, workProductsByPhase]);
    const recommendedRows = optionalCatalogProducts.flatMap(workProduct => {
        if (workProductStage(workProduct) !== selectedStage) return [];
        const scope = scopeForOptionalWorkProduct(workProduct, optionalScope);
        if (!scope) return [];
        const fallbackReady = Boolean(workProduct.hasContent || workProduct.state === "editable");
        const state = getOptionalWorkProductState?.(workProduct, scope) || { state: fallbackReady ? ("ready" as const) : ("idle" as const) };
        return [{ workProduct, scope, state, ready: state.state === "ready" || (state.state !== "generating" && fallbackReady) }];
    });
    const recommendedToBuild = recommendedRows.filter(row => row.state.state === "idle" && !row.ready);
    const recommendedToAccept = recommendedRows.filter(row => row.ready && !row.workProduct.approved);

    const selectFeature = (selected: any) => {
        onStorySelect?.(null);
        onOptionalStorySelect?.(null);
        onFeatureSelect?.(selected);
        onOptionalFeatureSelect?.(selected);
    };
    const selectStory = (selected: any, selectedFeature?: any) => {
        onStorySelect?.(selected);
        onOptionalStorySelect?.(selected, selectedFeature);
    };
    const selectStage = (stage: SddStageKey) => {
        if (stage === "implement" && canImplement) setSkipReviewOpen(true);
        else setSelectedStage(stage);
    };

    const approveOne = async (workProduct: WorkProduct, approved: boolean, scope: GenerationScope) => {
        const persisted = await onApprove(workProduct, approved, scope);
        if (persisted !== false) await onReviewsChanged?.();
        return persisted;
    };

    const approveAll = async (
        rows: Array<{ workProduct: WorkProduct; scope: GenerationScope }>,
        setPending: React.Dispatch<React.SetStateAction<"build" | "accept" | null>>
    ) => {
        setPending("accept");
        try {
            const results = await Promise.all(
                rows.map(async row => {
                    try {
                        return (await onApprove(row.workProduct, true, row.scope)) !== false;
                    } catch {
                        return false;
                    }
                })
            );
            await onReviewsChanged?.();
            const failed = results.filter(result => !result).length;
            if (failed > 0) toast.error(`${failed} Work Product${failed === 1 ? "" : "s"} could not be accepted.`);
            else toast.success(`Accepted ${rows.length} Work Product${rows.length === 1 ? "" : "s"}.`);
        } finally {
            setPending(null);
        }
    };

    const buildRequired = async () => {
        setRequiredPending("build");
        try {
            await Promise.all(requiredToBuild.map(row => Promise.resolve(onGenerate(row.workProduct!, row.scope))));
        } finally {
            setRequiredPending(null);
        }
    };
    const acceptRequired = async () => {
        await approveAll(
            requiredToAccept.map(row => ({ workProduct: row.workProduct!, scope: row.scope })),
            setRequiredPending
        );
    };
    const buildRecommended = async () => {
        setRecommendedPending("build");
        try {
            await Promise.all(recommendedToBuild.map(row => Promise.resolve(onGenerateOptional?.(row.workProduct, row.scope))));
        } finally {
            setRecommendedPending(null);
        }
    };
    const acceptRecommended = async () => {
        await approveAll(
            recommendedToAccept.map(row => ({ workProduct: row.workProduct, scope: row.scope })),
            setRecommendedPending
        );
    };
    const driftCount = requiredWorkProducts.filter(workProduct => workProduct.needs_rebuild).length;
    const activeScopeProgress = progressForScope(progress, feature, story);
    const scopedStageProgress = activeScopeProgress?.stages.find(stage => stage.key === selectedStage);
    const activeStageProgress = useMemo(() => {
        if (!progress) return scopedStageProgress;

        const featureId = feature ? entityId(feature) : "";
        const storyId = story ? entityId(story) : "";
        const cumulativeRows = progress.rows.filter(row => {
            if (row.stage !== selectedStage) return false;
            if (row.level === "PRODUCT") return true;
            if (row.level === "FEATURE") return Boolean(featureId && String(row.feature_id || "") === featureId);
            return Boolean(
                storyId &&
                    row.level === "USERSTORY" &&
                    String(row.feature_id || "") === featureId &&
                    String(row.story_id || "") === storyId
            );
        });
        const accepted = cumulativeRows.filter(row => row.status === "accepted").length;
        const inProgress = cumulativeRows.filter(row => row.status === "in_progress").length;
        const total = SDD_REQUIRED_WORK_PRODUCTS[selectedStage].length;
        return {
            ...scopedStageProgress,
            key: selectedStage,
            title: STAGE_TITLES[selectedStage],
            order: SDD_STAGES.findIndex(stage => stage.key === selectedStage) + 1,
            total,
            accepted,
            in_progress: inProgress,
            to_do: total - accepted - inProgress,
            percent: total ? Math.round((accepted / total) * 100) : 0
        };
    }, [feature, progress, scopedStageProgress, selectedStage, story]);

    useEffect(
        () => () => {
            onScopePickerOpenChange?.(false);
        },
        [onScopePickerOpenChange]
    );

    if (hierarchyPageOpen) {
        return (
            <Box id="sdd-panel" role="tabpanel" aria-label="Product, feature, and story hierarchy" sx={{ height: "100%", overflow: "hidden", bgcolor: "#fff" }}>
                <ProductHierarchyPage
                    onClose={() => setHierarchyPageOpen(false)}
                    project={project}
                    progress={progress}
                    onFeatureSelect={selectFeature}
                    onStorySelect={selectStory}
                />
            </Box>
        );
    }

    if (scopePickerPageOpen) {
        return (
            <Box id="sdd-panel" role="tabpanel" aria-label="Feature and story selection" sx={{ height: "100%", overflow: "hidden", bgcolor: "#fff" }}>
                <FeatureStorySelectionPage
                    onClose={() => {
                        setScopePickerPageOpen(false);
                    }}
                    project={project}
                    progress={progress}
                    selectedFeature={feature}
                    selectedStory={story}
                    onFeatureSelect={selectFeature}
                    onStorySelect={(selectedFeature, selectedStory) => {
                        selectFeature(selectedFeature);
                        selectStory(selectedStory, selectedFeature);
                    }}
                />
            </Box>
        );
    }

    if (progressReportPageOpen && loadProgressReport && exportProgressReport) {
        return (
            <Box id="sdd-panel" role="tabpanel" aria-label="SDD progress report" sx={{ height: "100%", overflow: "hidden", bgcolor: "#fff" }}>
                <SddProgressReportPage
                    onClose={() => setProgressReportPageOpen(false)}
                    loadPage={loadProgressReport}
                    exportXlsx={exportProgressReport}
                />
            </Box>
        );
    }

    return (
        <Box
            id="sdd-panel"
            role="tabpanel"
            aria-label="Specification-driven development workspace"
            sx={{
                position: "relative",
                height: "100%",
                overflow: "auto",
                bgcolor: "#fcfcfd",
                [LAPTOP_FIT_QUERY]: { overflow: "hidden" }
            }}
        >
            <Box
                sx={{
                    p: { xs: "16px 14px 24px", md: "20px 24px 28px" },
                    [LAPTOP_FIT_QUERY]: {
                        height: "100%",
                        minHeight: 0,
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        p: "10px 16px 12px"
                    }
                }}
            >
                <FeatureStudioWorkflowOverview
                    project={project}
                    feature={feature}
                    story={story}
                    progress={progress}
                    progressLoading={progressLoading}
                    contextMesh={contextMesh}
                    onOpenScopePicker={() => {
                        setScopePickerPageOpen(true);
                    }}
                    onOpenHierarchy={() => {
                        setHierarchyPageOpen(true);
                        onHierarchyPageOpen?.();
                    }}
                    onOpenProgressReport={() => setProgressReportPageOpen(true)}
                />

                <Box sx={{ position: "relative", mt: { xs: 3.5, lg: 0.5 }, [LAPTOP_FIT_QUERY]: { mt: 0, flex: 1, minHeight: 0 } }}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "260px minmax(0,1fr)" },
                            gap: 2,
                            alignItems: "stretch",
                            [LAPTOP_FIT_QUERY]: { height: "100%", minHeight: 0, gap: 1.25 }
                        }}
                    >
                        <ProgressHierarchy project={project} feature={feature} story={story} progress={progress} />
                        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", [LAPTOP_FIT_QUERY]: { minHeight: 0 } }}>
                            <Box sx={{ overflowX: "auto", overflowY: "hidden", flexShrink: 0 }}>
                                <SddWorkflowStepper
                                    selected={selectedStage}
                                    completedDrafts={completedDrafts}
                                    onSelect={selectStage}
                                    driftCount={driftCount}
                                />
                            </Box>
                            {!canImplement && selectedStage === "implement" && (
                                <Alert severity="warning" icon={<LockOutlined />} sx={{ mt: 1 }}>
                                    {activeLevel === "story"
                                        ? "Implementation is locked until first drafts for Specify, Plan, and Define Tasks are complete."
                                        : "Select a User Story before starting implementation."}
                                </Alert>
                            )}
                            <Box
                                sx={{
                                    position: "relative",
                                    zIndex: 4,
                                    mt: "-1px",
                                    flex: 1,
                                    border: "1px solid #103699",
                                    borderRadius: "0 0 10px 10px",
                                    bgcolor: "background.paper",
                                    p: 2,
                                    containerType: "inline-size",
                                    [LAPTOP_FIT_QUERY]: {
                                        minHeight: 0,
                                        overflow: "hidden",
                                        display: "flex",
                                        flexDirection: "column",
                                        p: 1.25
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "minmax(0,51fr) minmax(0,49fr)",
                                        gap: 3,
                                        [SDD_WORK_PRODUCT_STACK_QUERY]: {
                                            gridTemplateColumns: "minmax(0,1fr)",
                                            overflowY: "auto"
                                        },
                                        [LAPTOP_FIT_QUERY]: { flex: 1, minHeight: 0, gap: 1.5 }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            minWidth: 0,
                                            [LAPTOP_FIT_QUERY]: { minHeight: 0, overflow: "hidden" },
                                            [SDD_WORK_PRODUCT_STACK_QUERY]: { overflow: "visible" }
                                        }}
                                    >
                                        <SddRequiredWorkProducts
                                            stage={selectedStage}
                                            activeLevel={activeLevel}
                                            sources={requiredWorkProducts}
                                            project={project}
                                            feature={feature}
                                            story={story}
                                            headerActions={
                                                <Box
                                                    data-testid="required-work-products-actions"
                                                    sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, minWidth: 0 }}
                                                >
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        disabled={!requiredToBuild.length || requiredPending !== null || implementationLocked}
                                                        startIcon={requiredPending === "build" ? <CircularProgress size={11} color="inherit" /> : undefined}
                                                        onClick={() => void buildRequired()}
                                                        sx={{ minWidth: 84, width: "auto", height: 31, px: 1.5, flexShrink: 0, whiteSpace: "nowrap", fontSize: 11.5, fontWeight: 600, textTransform: "none" }}
                                                    >
                                                        Build All
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        disabled={!requiredToAccept.length || requiredPending !== null}
                                                        startIcon={requiredPending === "accept" ? <CircularProgress size={11} color="inherit" /> : undefined}
                                                        onClick={() => void acceptRequired()}
                                                        sx={{ minWidth: 84, width: "auto", height: 31, px: 1.5, flexShrink: 0, whiteSpace: "nowrap", fontSize: 11.5, fontWeight: 600, textTransform: "none" }}
                                                    >
                                                        Accept All
                                                    </Button>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: "1 1 190px" }}>
                                                        <ProgressDonut percent={activeStageProgress?.percent || 0} size={32} fontSize={8} />
                                                        <Typography noWrap sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", fontSize: 11.5, fontWeight: 500, color: "#0f172a" }}>
                                                            {STAGE_TITLES[selectedStage]} work products completed — {activeStageProgress?.accepted || 0} of{" "}
                                                            {activeStageProgress?.total || 0}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            }
                                            getState={getWorkProductState}
                                            progress={progress}
                                            onGenerate={onGenerate}
                                            onOpen={onOpen}
                                            onApprove={(workProduct, approved, scope) => scope && approveOne(workProduct, approved, scope)}
                                            bridgePhase={selectedDefinition.phase}
                                        />
                                    </Box>
                                    <RecommendedWorkProductsPanel
                                        stage={selectedStage}
                                        rows={recommendedRows}
                                        loading={optionalWorkProductsLoading}
                                        pending={recommendedPending}
                                        onGenerate={onGenerateOptional}
                                        onOpen={onOpen}
                                        onApprove={approveOne}
                                        onBuildAll={() => void buildRecommended()}
                                        onAcceptAll={() => void acceptRecommended()}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Dialog
                open={skipReviewOpen}
                onClose={() => setSkipReviewOpen(false)}
                transitionDuration={reduceMotion ? 0 : undefined}
                aria-labelledby="skip-sdd-review-title"
            >
                <DialogTitle id="skip-sdd-review-title" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <WarningAmber color="warning" />
                    Begin implementation without review?
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        The required first drafts are complete, but one or more Specify, Plan, or Define Tasks stages have not been reviewed. You can
                        continue, but unresolved assumptions may create specification drift.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSkipReviewOpen(false)}>Return to review</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => {
                            setSkipReviewOpen(false);
                            setSelectedStage("implement");
                        }}
                    >
                        Skip review and continue
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
